/**
 * Shared types + the payload-building helper for the `QueueJoinPanel` seam
 * (CU-86cb7ewja) — extracted from `queue-join-panel.tsx` (max-lines split) so
 * `queue-join-panel.tsx`, `queue-identity-fields.tsx`, and
 * `queue-first-join-block.tsx` can all depend on ONE shared source without a
 * circular import between the main component and its extracted pieces.
 */
import {
  type QueueAction,
  type QueueEntryView,
  type ReservationQueueListLabels,
  type ReservationQueueListPermissions,
} from '../components/reservation-queue-list';

export interface QueueJoinClientOption {
  value: string;
  label: string;
  /** The client's configured brands — filters the brand select once a client is picked. */
  brands: string[];
}

export interface QueueJoinOfferOption {
  value: string;
  label: string;
  clientId: number;
  brand: string | null;
}

export type QueueJoinIdentity =
  /**
   * `label` is the visible, already-selected identity summary (e.g. offer
   * title · client · brand) — the trigger always shows it; "Zmień" is the
   * only way to change it, never a locked/non-editable readout.
   */
  | { mode: 'offer'; offerId: number; label: string }
  | { mode: 'standalone' }
  /**
   * The caller already holds the active reservation on this cell (the "own
   * reservation" state) — even with zero existing queue entries, this mode
   * shows the collapsed "Dodaj wpis dla innego klienta" toggle rather than an
   * always-open identity form, matching `standalone`'s first-join behavior
   * only once at least one own entry (or an explicit toggle click) exists.
   */
  | { mode: 'own-reservation' };

export interface QueueJoinPayload {
  offerId?: number;
  clientId?: number;
  brand?: string | null;
  /** REQUIRED shared "do kiedy" deadline (CU-86cbc4t4q) — date-only `YYYY-MM-DD`. */
  ttlExpiresAt: string;
}

export interface QueueJoinPanelLabels {
  addAnotherClientLabel: string;
  newEntryTitle: string;
  cancelLabel: string;
  clientLabel: string;
  brandLabel: string;
  offerLabel: string;
  clientPlaceholder: string;
  brandPlaceholder: string;
  offerPlaceholder: string;
  clientSearchPlaceholder: string;
  brandSearchPlaceholder: string;
  offerSearchPlaceholder: string;
  brandRequiredLabel: string;
  noBrandsLabel: string;
  joinLabel: string;
  /** "Zmień" — opens the identity fields to change the offer-scoped identity trigger. */
  changeIdentityLabel: string;
  /** Shared expiry date control label/hint (task 4.3) — reuses the SAME wording as the
   * other two bulk/manual entry points so all three "do kiedy" controls read identically. */
  expiryLabel: string;
  expiryHint: string;
  queueListLabels: ReservationQueueListLabels;
}

export interface QueueJoinPanelProps {
  testId: string;
  /** Namespaces the rendered `ReservationQueueList`'s own test ids — no other semantic meaning. */
  queueListId: number;
  ownEntries: QueueEntryView[];
  currentUserId: number;
  permissions: ReservationQueueListPermissions;
  identity: QueueJoinIdentity;
  clientOptions: QueueJoinClientOption[];
  offerOptions?: QueueJoinOfferOption[];
  onJoin: (payload: QueueJoinPayload) => void;
  onQueueAction: (action: QueueAction, entryId: number) => void;
  isJoinPending?: boolean;
  labels: QueueJoinPanelLabels;
}

/** Builds the join-request payload from picked client/brand/offer identity fields,
 * or `null` while no client is picked yet (nothing valid to submit). */
export function buildQueueJoinPayload(
  clientId: string,
  brand: string,
  offerId: string,
  ttlExpiresAt: string,
): QueueJoinPayload | null {
  if (!clientId) return null;
  if (offerId) return { offerId: Number(offerId), ttlExpiresAt };
  return { clientId: Number(clientId), brand: brand || null, ttlExpiresAt };
}
